/* eslint-disable @typescript-eslint/no-unused-vars */
import { IScatterPoint } from "./useScatterChart";

import { getColor } from "../../../../../../../../../utils/getColor";
import { truncateText } from "../utils";

interface IHoveredCluster {
	points: IScatterPoint[];
	centerX: number;
	centerY: number;
}

interface DbScanResult {
	clusterAssignments: (number | undefined)[]; // Cluster index for each point
	clusters: { [key: number]: number[] };
	noisePoints: number[];
}

export const _avatarImages = {};

export const axisLabels = {
	id: "axisLabels",
	beforeDatasetsDraw(chart, args, options) {
		const {
			ctx,
			chartArea: { left, bottom, width, height },
			options: {
				layout: {
					padding: { bottom: paddingBottom, top: paddingTop },
				},
			},
		} = chart;

		const labelLeft = 24;
		const labelBottom = bottom + paddingBottom;

		ctx.save();
		ctx.fillStyle = options.fontColor;
		ctx.font = `${options.fontSize}px Source Sans Pro`;
		ctx.textAlign = "center";

		// label the middle of the x axis
		ctx.textBaseline = "bottom";
		const truncatedXText = `${truncateText(ctx, options.xAxisLabel, width * 0.7)}${
			options.xAggregateLabel ? ` ${options.xAggregateLabel}` : ""
		}`;
		ctx.fillText(truncatedXText, width / 2 + left, labelBottom);
		ctx.rotate(-Math.PI / 2);
		// label the middle of the y axis
		ctx.textBaseline = "top";
		const truncatedYText = `${truncateText(ctx, options.yAxisLabel, height * 0.7)}${
			options.yAggregateLabel ? ` ${options.xAggregateLabel}` : ""
		}`;
		const labelMiddleY = height / 2 + paddingTop; // Calculate the middle Y position:
		ctx.fillText(truncatedYText, -labelMiddleY, labelLeft); // Adjust the Y position
		ctx.restore();
	},
};

const drawAvatarImage = (canvas, pointImage) => {
	const ctx = canvas.getContext("2d");

	// make the canvas circular and the same size as the original image
	ctx.beginPath();
	ctx.arc(pointImage.width / 2, pointImage.height / 2, pointImage.width / 2, 0, 2 * Math.PI);

	ctx.clip();

	// draw the image in the center of the canvas with desired width and height
	ctx.drawImage(pointImage, 0, 0, pointImage.width, pointImage.height);
};

export const getAvatarImage = (avatarURL, avatarCache) => {
	// the canvas element
	const canvas = document.createElement("canvas");

	if (!avatarURL) {
		canvas.width = 200;
		canvas.height = 200;
		const ctx = canvas.getContext("2d");
		if (!ctx) return canvas;
		ctx.beginPath();
		ctx.arc(100, 100, 100, 0, 2 * Math.PI);
		ctx.clip();
		ctx.fillStyle = "#9197A1";
		ctx.fill();
		// add the "-" in the middle of the circle
		ctx.font = "140px Source Sans Pro";
		ctx.fillStyle = "#FFFFFF";
		ctx.textAlign = "center";
		ctx.textBaseline = "middle";
		ctx.fillText("-", 100, 100);
		ctx.stroke();
		return canvas;
	} else if (Object.keys(_avatarImages).includes(avatarURL)) {
		return _avatarImages[avatarURL];
	} else {
		const pointImage = avatarCache[avatarURL];
		if (pointImage) {
			canvas.width = pointImage.width;
			canvas.height = pointImage.height;
			drawAvatarImage(canvas, pointImage);
			_avatarImages[avatarURL] = canvas;
		}

		return canvas;
	}
};

export const getAvatarScatterPoint = (point, diameter, active, avatarCache) => {
	const pointType = point.type;

	if (pointType === "group") {
		return getGroupScatterPoint({ raw: point });
	} else if (pointType === "employee") {
		const avatar = getAvatarImage(point?.avatar, avatarCache);
		const avatarCtx = avatar.getContext("2d");
		// draw a white border around the edge of the circle, on top of the image only if the image has loaded
		const abc = point.activeBorderColor && active;
		const activeWidth = abc ? 3 : 4;
		const ultimateLineWidth = active ? activeWidth : 2;
		avatarCtx.lineWidth = (ultimateLineWidth * avatar.width) / (diameter + ultimateLineWidth);
		// if active (hovered) and activeBorderColor is defined, use that color, otherwise use white
		avatarCtx.strokeStyle = abc ? point.activeBorderColor : "#FFFFFF";
		// move the point inward, along the inside edge of the circumference
		avatarCtx.translate(avatar.width / 2, avatar.height / 2);
		avatarCtx.stroke();
		return avatar;
	} else {
		const canvas = document.createElement("canvas");
		const ctx = canvas.getContext("2d");
		if (!ctx) return canvas;
		canvas.width = diameter;
		canvas.height = diameter;
		// make the canvas circular
		ctx.beginPath();
		ctx.arc(diameter / 2, diameter / 2, diameter / 2, 0, 2 * Math.PI);
		ctx.clip();
		// draw the background color
		ctx.fillStyle = point.backgroundColor ? point.backgroundColor : getColor("primary", 3);
		ctx.fillRect(0, 0, diameter, diameter);
		// draw a border circle if active
		if (active) {
			ctx.lineWidth = 2;
			ctx.strokeStyle = point.activeBorderColor ? point.activeBorderColor : getColor("primary", 5);
			ctx.stroke();
		}
		return canvas;
	}
};

const getGroupScatterPoint = (context) => {
	const diameter = 32;
	const backgroundColor = context.raw["backgroundColor"] || "#FFFFFF";
	const color = context.raw["color"] || getColor("primary", 5);
	const groupName = context.raw["name"];

	// initials are the first letter of the first words if there are multiple words
	// also they are all capitalized
	const initialsArray = !groupName
		? ["-"]
		: groupName
				?.split(" ")
				.map((word: string) => word[0].toUpperCase())
				.slice(0, 2);
	// or the first two letters if there is only one word
	if (initialsArray?.length === 1 && groupName?.length > 1 && initialsArray[0] !== "-") {
		initialsArray.push(groupName[1].toUpperCase());
	}

	// the canvas element
	const canvas = document.createElement("canvas");

	canvas.width = diameter;
	canvas.height = diameter;

	canvas.style.width = `${diameter}px`;
	canvas.style.height = `${diameter}px`;

	// get the canvas context
	const ctx = canvas.getContext("2d");

	if (!ctx) return canvas;

	// make the canvas circular
	ctx.beginPath();
	ctx.arc(diameter / 2, diameter / 2, diameter / 2, 0, 2 * Math.PI);
	ctx.clip();

	// draw the background color
	ctx.fillStyle = backgroundColor;
	ctx.fillRect(0, 0, diameter, diameter);

	// draw the group initials
	ctx.fillStyle = color;
	ctx.font = "14px Source Sans Pro";
	ctx.textAlign = "center";
	ctx.textBaseline = "middle";
	ctx.fillText(
		initialsArray?.join(""),
		diameter / 2,
		diameter / 2 + 1 // Adjusted y-coordinate
	);

	// return the canvas
	return canvas;
};

// Helper function to create a cluster indicator image
const createClusterIndicator = (
	count,
	diameter,
	backgroundColor = getColor("primary", 3),
	activeBorderColor: string | undefined = undefined
) => {
	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");

	// Set canvas size
	canvas.width = diameter;
	canvas.height = diameter;

	if (!ctx) return canvas;
	ctx.imageSmoothingQuality = "high";
	// Draw circle background
	ctx.beginPath();
	ctx.arc(diameter / 2, diameter / 2, diameter / 2, 0, Math.PI * 2);
	ctx.fillStyle = backgroundColor;
	ctx.fill();

	// Draw count text
	ctx.fillStyle = "#ffffff";
	ctx.font = `${diameter / 2}px Arial`;
	ctx.textAlign = "center";
	ctx.textBaseline = "middle";
	ctx.fillText(count.toString(), diameter / 2, diameter / 2 + 1);

	// Draw border if active
	if (activeBorderColor) {
		ctx.lineWidth = 1;
		ctx.strokeStyle = activeBorderColor;
		ctx.beginPath();
		ctx.arc(diameter / 2, diameter / 2, diameter / 2 - 1, 0, Math.PI * 2);
		ctx.stroke();
	}
	return canvas;
};

export const getPointTypeFromDataseries = (dataSeries?: string[]) => {
	if (!dataSeries) return null;
	if (["group", "employee"].includes(dataSeries[2].toLowerCase())) return dataSeries[2].toLowerCase();
	if (dataSeries[2].toLowerCase() === "subject") return "employee";
	if (dataSeries[2].toLowerCase() === "manager") return "employee";
	return "point";
};

export const getAvatarScatterPointFn = (avatarCache) => ({
	id: "avatarScatterPoints",
	// Convert data coordinates to pixel coordinates
	getPixelPoint(chart, dataPoint) {
		const xScale = chart.scales.x;
		const yScale = chart.scales.y;
		return {
			x: xScale.getPixelForValue(dataPoint.x),
			y: yScale.getPixelForValue(dataPoint.y),
		};
	},
	// DBSCAN clustering algorithm: https://en.wikipedia.org/wiki/DBSCAN
	// It's basically a depth-first search that groups points based on their distance (Ecuclidean distance)
	// eps is the maximum distance between two points in pixel for them to be considered as in the same neighborhood since we convert all points to pixel coordinates
	dbScan(points, epsilon, minPts, chart): DbScanResult {
		// holds the cluster index for each point
		const clusterAssignments = new Array(points.length).fill(undefined);
		let clusterIndex = 0;

		// Convert all points to pixel coordinates once
		const pixelPoints = points.map((point) => this.getPixelPoint(chart, point));

		function getNeighbors(pointIndex) {
			const neighbors: number[] = [];
			const point = pixelPoints[pointIndex];

			for (let i = 0; i < points.length; i++) {
				if (i !== pointIndex) {
					const distance = Math.sqrt(
						Math.pow(pixelPoints[i].x - point.x, 2) + Math.pow(pixelPoints[i].y - point.y, 2)
					);
					if (distance <= epsilon) {
						neighbors.push(i);
					}
				}
			}
			return neighbors;
		}

		function expandCluster(pointIndex, neighbors) {
			clusterAssignments[pointIndex] = clusterIndex;

			for (let i = 0; i < neighbors.length; i++) {
				const neighborIndex = neighbors[i];

				if (clusterAssignments[neighborIndex] === undefined) {
					clusterAssignments[neighborIndex] = clusterIndex;

					const newNeighbors = getNeighbors(neighborIndex);
					if (newNeighbors.length >= minPts) {
						neighbors.push(...newNeighbors.filter((n) => !neighbors.includes(n)));
					}
				}
			}
		}

		// Main DBSCAN loop
		for (let i = 0; i < points.length; i++) {
			if (clusterAssignments[i] !== undefined) continue;

			const neighbors = getNeighbors(i);

			if (neighbors.length < minPts) {
				clusterAssignments[i] = -1; // Noise point
			} else {
				expandCluster(i, neighbors);
				clusterIndex++;
			}
		}

		// Group points by cluster
		const clusters: { [key: number]: number[] } = {};
		clusterAssignments.forEach((label, index) => {
			if (label === -1) return; // Skip noise points
			if (!clusters[label]) clusters[label] = [];
			clusters[label].push(index);
		});

		return {
			clusterAssignments,
			clusters,
			noisePoints: clusterAssignments
				.map((label, index) => (label === -1 ? index : null))
				.filter((x) => x !== null),
		};
	},
	afterDatasetsDraw(chart) {
		const {
			ctx,
			scales: { x, y },
			data: { datasets },
		} = chart;

		const avatarPoints: IScatterPoint[] = [...datasets[0].data];

		const hoveredIndex: number = chart.getActiveElements()[0]?.index;
		const epsilon = 35;
		const minPts = 1;
		const { clusters, noisePoints } = this.dbScan(avatarPoints, epsilon, minPts, chart);

		let hoveredCluster: IHoveredCluster | null = null;
		let hoveredPoint: IScatterPoint | null = null;

		if (hoveredIndex >= 0) {
			// Check if hovered point is in a cluster
			const clusterEntry = Object.entries(clusters).find(([_, indices]) =>
				(indices as number[]).includes(hoveredIndex)
			);

			if (clusterEntry) {
				hoveredCluster = {
					points: clusterEntry[1].map((i) => avatarPoints[i]),
					centerX: clusterEntry[1].reduce((sum, i) => sum + avatarPoints[i].x, 0) / clusterEntry[1].length,
					centerY: clusterEntry[1].reduce((sum, i) => sum + avatarPoints[i].y, 0) / clusterEntry[1].length,
				};
			} else if (noisePoints.includes(hoveredIndex)) {
				hoveredPoint = avatarPoints[hoveredIndex];
			}
		}

		// Draw noise points (points without clusters)
		noisePoints.forEach((index) => {
			const point = avatarPoints[index];
			let diameter = 12;
			switch (point.type) {
				case "group":
					diameter = 35;
					break;
				case "employee":
					diameter = 24;
					break;
				default:
					break;
			}

			const avatar = getAvatarScatterPoint(point, diameter, false, avatarCache);
			ctx.imageSmoothingQuality = "high";
			ctx.drawImage(
				avatar,
				0,
				0,
				avatar.width,
				avatar.height,
				x.getPixelForValue(point.x) - diameter / 2,
				y.getPixelForValue(point.y) - diameter / 2,
				diameter,
				diameter
			);
		});

		// Draw clustered points
		Object.values(clusters).forEach((clusterIndices) => {
			// Calculate cluster center
			const clusterPoints = clusterIndices.map((i) => avatarPoints[i]);
			const centerX = clusterPoints.reduce((sum, p) => sum + p.x, 0) / clusterPoints.length;
			const centerY = clusterPoints.reduce((sum, p) => sum + p.y, 0) / clusterPoints.length;

			// Determine cluster diameter based on number of points
			const diameter = 24; // base diameter for clusters

			// Create and draw cluster indicator
			const clusterImage = createClusterIndicator(clusterPoints.length, diameter);
			ctx.drawImage(
				clusterImage,
				0,
				0,
				diameter,
				diameter,
				x.getPixelForValue(centerX) - diameter / 2,
				y.getPixelForValue(centerY) - diameter / 2,
				diameter,
				diameter
			);
		});

		// Draw hovered
		if (hoveredCluster) {
			const diameter = 35;
			// Get border color
			const activeBorderColor = hoveredCluster.points[0].activeBorderColor;

			const clusterImage = createClusterIndicator(
				hoveredCluster.points.length,
				diameter,
				undefined,
				activeBorderColor
			);

			ctx.drawImage(
				clusterImage,
				0,
				0,
				diameter,
				diameter,
				x.getPixelForValue(hoveredCluster.centerX) - diameter / 2,
				y.getPixelForValue(hoveredCluster.centerY) - diameter / 2,
				diameter,
				diameter
			);
		} else if (hoveredPoint) {
			// Draw hovered individual avatar
			const diameter = hoveredPoint.type === "point" ? 16 : 35;
			const avatar = getAvatarScatterPoint(hoveredPoint, diameter, true, avatarCache);

			ctx.imageSmoothingQuality = "high";
			ctx.drawImage(
				avatar,
				0,
				0,
				avatar.width,
				avatar.height,
				x.getPixelForValue(hoveredPoint.x) - diameter / 2,
				y.getPixelForValue(hoveredPoint.y) - diameter / 2,
				diameter,
				diameter
			);
		}
	},
});
